import React, { useCallback, useEffect, useState } from 'react'
import request from 'superagent'
import { Formik } from 'formik'
import * as yup from 'yup'
import { pathOr, isEmpty } from 'ramda'
import { Helmet } from 'react-helmet'
import { CLOUDINARY_UPLOAD_PRESET, CLOUDINARY_UPLOAD_URL } from 'constants/general'
import { Header, FormLoader } from '../../../../components'
import FormErrors from '../../../molecules/FormErrors/FormErrors'
import LogoEditor from './LogoEditor/LogoEditor'
import ThemeSelector from './ThemeSelector/ThemeSelector'
import ThemePreview from './ThemePreview'
import styles from './OnboardingBrandingForm.module.scss'
import Paragraph from '../../../atoms/Typography/Paragraph/Paragraph'
import { agencyApiEvents } from '../../../../../modules/api/agency'
import SettingIcon from 'components/atoms/Icons/Controls/Settings'
import { TAgencyTheme } from 'modules/api/agency/types'

interface OnboardingBrandingFormProps {
  getThemes: () => void
  themes: TAgencyTheme[]
  handleSubmit: (values: any) => void
  initialValues: {
    logo: string
    /** Undefined only if the theme list has not loaded yet — see `getBrandingInitialValues`. */
    theme?: TAgencyTheme
  }
  isSubmitting?: boolean
  isLoading?: boolean
  getGeneralFormErrorsByEvent: (event: any) => Array<any>
  getFormFieldErrorsByEvent: (event: any) => Record<string, any>
  currentAgencyId?: string | null
}

const validationSchema = yup.object().shape({
  logo: yup.string().required('Logo is required, please upload and save.'),
  theme: yup.object().required('required.'),
})

const ButtonContainer = ({ children }: { children: React.ReactNode }): React.ReactElement => (
  <section className={styles['button-container']}>{children}</section>
)

const OnboardingBrandingForm: React.FC<OnboardingBrandingFormProps> = ({
  getThemes,
  themes,
  handleSubmit,
  initialValues,
  isSubmitting,
  isLoading,
  getGeneralFormErrorsByEvent,
  getFormFieldErrorsByEvent,
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [uploadedFileCloudinaryUrl, setUploadedFileCloudinaryUrl] = useState('')
  const [cropZoom, setCropZoom] = useState(1)
  const [position, setPosition] = useState({ x: 0.5, y: 0.5 })
  const [editing, setEditing] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)

  useEffect(() => {
    getThemes()
  }, [getThemes])

  const handleImageDrop = useCallback((files: File[]) => {
    setUploadedFile(files[0])
  }, [])

  const handleClear = useCallback(() => {
    setUploadedFile(null)
  }, [])

  const handleSave = useCallback(
    (editor: any, setFieldValue: any) => {
      if (editor) {
        setUploadProgress(1)
        const canvas = editor.getImageScaledToCanvas()
        canvas.toBlob((blob: any) => {
          const file = new File([blob], uploadedFile!.name, { type: uploadedFile!.type, lastModified: Date.now() })

          const upload = request
            .post(CLOUDINARY_UPLOAD_URL)
            .field('upload_preset', CLOUDINARY_UPLOAD_PRESET)
            .field('file', file)
            .on('progress', (e: any) => setUploadProgress(e.percent))

          upload.end((err: any, response: any) => {
            if (err) {
              console.error('err', err)
            }

            if (response.body.secure_url !== '') {
              setEditing(false)
              setUploadProgress(null)
              setUploadedFileCloudinaryUrl(response.body.secure_url)
              setFieldValue('logo', response.body.secure_url)
            }
          })
        })
      }
    },
    [uploadedFile],
  )

  const handleEdit = useCallback(() => {
    setEditing(true)
  }, [])

  const handleZoomChange = useCallback((zoom: number) => {
    setCropZoom(zoom / 25)
  }, [])

  const handlePositionChange = useCallback((pos: { x: number; y: number }) => {
    setPosition(pos)
  }, [])

  const getFieldError = useCallback((path: string, touched: any, errors: any): string => {
    return pathOr(false, path.split('.'), touched) ? (pathOr('', path.split('.'), errors) as string) : ''
  }, [])

  return (
    <Formik
      validationSchema={validationSchema}
      validateOnBlur={false}
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit: formikHandleSubmit, values, setFieldValue, errors, touched }) => {
        const submitEvent = agencyApiEvents.updateBranding_request(values)
        const generalErrors = getGeneralFormErrorsByEvent(submitEvent)
        const fieldErrors = getFormFieldErrorsByEvent(submitEvent)
        const hasApiErrors = generalErrors.length > 0 || !isEmpty(fieldErrors)

        const formLoaderState = isLoading
          ? 'loading'
          : isSubmitting
          ? 'submitting'
          : !isEmpty(errors) || hasApiErrors
          ? 'error'
          : undefined

        const logoError = getFieldError('logo', touched, errors)
        const themeError = getFieldError('theme', touched, errors)

        return (
          <FormLoader
            onSubmit={formikHandleSubmit}
            state={formLoaderState}
            buttonProps={{ children: 'Save' }}
            ButtonContainer={ButtonContainer}
          >
            <Helmet>
              <title>reOS | Settings | Branding</title>
            </Helmet>
            <section>
              <div className={styles.container}>
                <Header icon={<SettingIcon />} tag="h1" text="Branding" />
                <Paragraph size="body1">
                  Turn every interaction into a brand moment. Upload your logo and set your brand colour to enhance your
                  visibility across all system-generated communications.
                </Paragraph>
              </div>
            </section>

            <section>
              <div className={styles.container}>
                {logoError && <p className={styles.errors}>Logo is {logoError}</p>}
                <LogoEditor
                  uploadProgress={uploadProgress}
                  uploadedFileCloudinaryUrl={values.logo || uploadedFileCloudinaryUrl}
                  uploadedFile={uploadedFile}
                  editing={editing}
                  position={position}
                  cropZoom={cropZoom}
                  cloudinaryUploadPreset={CLOUDINARY_UPLOAD_PRESET}
                  onPositionChange={handlePositionChange}
                  onImageDrop={handleImageDrop}
                  onEdit={handleEdit}
                  onClear={handleClear}
                  onSave={handleSave}
                  onZoomChange={handleZoomChange}
                  setFieldValue={setFieldValue}
                  error={logoError}
                />
              </div>
            </section>

            <section>
              <div className={styles.container}>
                <div className="rbn--row">
                  <div className="rbn--col-sm-12 rbn--col-md-3">
                    <h3>Select Theme or Custom Colour</h3>
                  </div>
                </div>
                <div className="rbn--row">
                  <div className="rbn--col-sm-12 rbn--col-md-3">
                    <ThemeSelector
                      themes={themes}
                      currentTheme={values.theme}
                      onChange={(theme: any) => setFieldValue('theme', theme)}
                      // @ts-expect-error ts-migrate(2322) FIXME: Type '{ themes: any; currentTheme: any; onChange: ... Remove this comment to see the full error message
                      onClear={handleClear}
                      error={themeError}
                    />
                  </div>
                  <div className="rbn--col-sm-12 rbn--col-md-5">
                    <div className={styles['theme-preview']}>
                      <ThemePreview
                        color={pathOr('rgba(0, 0, 0, 1.0)', ['theme', 'primary'], values)}
                        name={pathOr('Custom', ['theme', 'name'], values)}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <FormErrors errors={[...generalErrors, ...Object.values(errors)]} />
            </section>
          </FormLoader>
        )
      }}
    </Formik>
  )
}

export default OnboardingBrandingForm
